import React, { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronDown, X } from 'lucide-react';
import useCloseOnWindowBlur from '../../hooks/useCloseOnWindowBlur';

const MAX_CHIPS = 3;

export default function MultiSelectDropdown({
    options = [], value, onChange, placeholder = 'Select…',
    className = '', style, disabled = false, id, maxSelections,
    onBlur, ariaInvalid, ariaDescribedBy,
}) {
    const [open, setOpen] = useState(false);
    const opts = useMemo(() => (options || []).filter(Boolean), [options]);
    const selected = Array.isArray(value) ? value : [];
    const atMax = maxSelections && selected.length >= maxSelections;

    // Embedded forms: a click on the host page never reaches this iframe's
    // document, so Radix can't dismiss. Close when the window loses focus and
    // fire the field's onBlur — focus was in the search box, so the trigger's
    // own blur (which marks the field touched and validates it) won't run.
    useCloseOnWindowBlur(open, () => {
        setOpen(false);
        onBlur?.();
    });

    const toggle = (o) => {
        if (selected.includes(o)) {
            onChange(selected.filter(s => s !== o));
        } else {
            if (atMax) return; // prevent selecting beyond max
            onChange([...selected, o]);
        }
    };

    return (
        <Popover.Root open={open} onOpenChange={setOpen} modal={false}>
            <Popover.Trigger asChild>
                <button
                    type="button" id={id} disabled={disabled}
                    role="combobox" aria-expanded={open} aria-haspopup="listbox"
                    aria-invalid={ariaInvalid || undefined}
                    aria-describedby={ariaDescribedBy || undefined}
                    onBlur={onBlur}
                    className={`flex items-center justify-between gap-2 text-left ${className}`}
                    style={style}
                >
                    <span className="flex flex-wrap items-center gap-1 min-w-0">
                        {selected.length === 0 && (
                            <span className="text-stone-400">{placeholder}</span>
                        )}
                        {selected.slice(0, MAX_CHIPS).map(o => (
                            <span key={o} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-stone-100 rounded">
                                {o}
                                <X className="w-3 h-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(o); }} />
                            </span>
                        ))}
                        {selected.length > MAX_CHIPS && (
                            <span className="text-xs text-stone-500">+{selected.length - MAX_CHIPS} more</span>
                        )}
                    </span>
                    <ChevronDown className="w-4 h-4 shrink-0 text-stone-400" />
                </button>
            </Popover.Trigger>
            {/* Deliberately NO Popover.Portal: content must remain inside the form's
                DOM so document.body.scrollHeight (which sizes the embed iframe via
                the ResizeObserver → postMessage bridge in PublicForm.jsx) accounts
                for the open dropdown. */}
            <Popover.Content
                align="start" sideOffset={4} collisionPadding={8}
                className="z-50 w-[var(--radix-popover-trigger-width)] rounded-lg border border-stone-200 bg-white shadow-xl"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <Command loop>
                    <Command.Input
                        autoFocus placeholder="Search…"
                        className="w-full px-3 py-2 text-sm border-b border-stone-100 focus:outline-none"
                    />
                    <Command.List className="max-h-60 overflow-y-auto p-1" role="listbox" aria-multiselectable="true">
                        <Command.Empty className="px-3 py-2 text-sm text-stone-400">No options found.</Command.Empty>
                        {opts.map(o => {
                            const isSelected = selected.includes(o);
                            const isDisabled = atMax && !isSelected;
                            return (
                                <Command.Item
                                    key={o} value={o}
                                    onSelect={() => !isDisabled && toggle(o)}
                                    disabled={isDisabled}
                                    className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} data-[selected=true]:bg-stone-100`}
                                >
                                    <span className={`flex items-center justify-center w-4 h-4 rounded border ${isSelected ? 'bg-stone-900 border-stone-900 text-white' : 'border-stone-300'}`}>
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </span>
                                    {o}
                                </Command.Item>
                            );
                        })}
                    </Command.List>
                    {selected.length > 0 && (
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            className="w-full px-3 py-2 text-xs text-left text-stone-500 border-t border-stone-100 hover:text-stone-700"
                        >
                            Clear all ({selected.length})
                        </button>
                    )}
                    {maxSelections && (
                        <div className="px-3 py-1.5 text-[10px] text-stone-400 border-t border-stone-100">
                            {selected.length} / {maxSelections} selected
                        </div>
                    )}
                </Command>
            </Popover.Content>
        </Popover.Root>
    );
}
